import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import useLandingAgentChat, {
  LANDING_AGENT_OPEN_EVENT,
  LAUNCHER_FALLBACK_PLACEHOLDER,
  openLandingAgent,
} from './useLandingAgentChat';
import LandingAgentChatOverlay from './LandingAgentChatOverlay';

/**
 * Строка-лаунчер AI в шапке лендинга (поверхность S6).
 * Владелец состояния чата: одна инстанция на страницу, overlay — её продолжение.
 * Открытие только через событие `mb-landing-open-agent` (лаунчер, hero, карточки ai-flow).
 */

const PLACEHOLDER_ROTATE_MS = 7000;
const BOOTSTRAP_DELAY_MS = 1200;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function LandingAgentLauncher() {
  const chat = useLandingAgentChat();
  const { ensureBootstrap, placeholders, ready, enabled } = chat;

  const [open, setOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const buttonRef = useRef(null);

  // Bootstrap отложен: не конкурирует с LCP hero, тексты подсказок нужны только для строки.
  useEffect(() => {
    let timeoutId = null;
    let idleId = null;
    const run = () => {
      ensureBootstrap().catch(() => undefined);
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: BOOTSTRAP_DELAY_MS });
    } else {
      timeoutId = setTimeout(run, BOOTSTRAP_DELAY_MS);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleId);
    };
  }, [ensureBootstrap]);

  useEffect(() => {
    setPlaceholderIndex(0);
  }, [placeholders]);

  useEffect(() => {
    if (placeholders.length < 2) return undefined;
    if (paused || open || prefersReducedMotion()) return undefined;
    const intervalId = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, PLACEHOLDER_ROTATE_MS);
    return () => clearInterval(intervalId);
  }, [placeholders, paused, open]);

  useEffect(() => {
    const handleOpen = (event) => {
      const prompt = typeof event?.detail?.prompt === 'string' ? event.detail.prompt : '';
      setInitialPrompt(prompt);
      setOpen(true);
    };
    window.addEventListener(LANDING_AGENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(LANDING_AGENT_OPEN_EVENT, handleOpen);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setInitialPrompt('');
    const node = buttonRef.current;
    if (node && typeof node.focus === 'function') node.focus();
  }, []);

  const placeholder = ready && !enabled
    ? 'Чат недоступен — начните бесплатно'
    : placeholders[placeholderIndex] || LAUNCHER_FALLBACK_PLACEHOLDER;
  const state = !ready ? 'loading' : open ? 'open' : enabled ? 'idle' : 'disabled';

  return (
    <div className="mb-launcher" data-state={state}>
      <button
        type="button"
        ref={buttonRef}
        className="mb-launcher__bar"
        onClick={() => openLandingAgent()}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Открыть AI-чат Markbase"
      >
        <span className="mb-launcher__icon" aria-hidden>
          <Sparkles size={16} />
        </span>
        <span className="mb-launcher__viewport">
          <span className="mb-launcher__text" key={placeholder}>
            {placeholder}
          </span>
        </span>
        <span className="mb-launcher__cta" aria-hidden>
          {state === 'loading' ? 'Загрузка' : state === 'disabled' ? 'Начать бесплатно' : 'Спросить AI'}
        </span>
      </button>

      <LandingAgentChatOverlay
        open={open}
        onClose={handleClose}
        chat={chat}
        initialPrompt={initialPrompt}
      />
    </div>
  );
}
